import { useState, useEffect } from 'react'
import { Plus, Pencil, Trash2, Search, X, Check, MapPin, Users, Star } from 'lucide-react'
import api from '../../../core/services/api'
import { Toast, ConfirmModal } from '../../../components/Toast'

interface SedeCliente {
  id: string
  cliente_id: string
  descrizione: string
  via: string | null
  citta: string | null
  cap: string | null
  provincia: string | null
  note: string | null
  is_preferita: boolean
  created_at: string
}

interface ReferenteCliente {
  id: string
  cliente_id: string
  nome: string
  cognome: string
  email: string | null
  telefono: string | null
  ruolo: string | null
  note: string | null
  is_preferito: boolean
  created_at: string
}

interface Cliente {
  id: string
  ragione_sociale: string
  p_iva: string | null
  via: string | null
  citta: string | null
  cap: string | null
  provincia: string | null
  email: string | null
  telefono: string | null
  attivo: boolean
  note: string | null
  sedi: SedeCliente[]
  referenti: ReferenteCliente[]
  created_at: string
}

interface ClienteForm {
  ragione_sociale: string
  p_iva: string
  via: string
  citta: string
  cap: string
  provincia: string
  email: string
  telefono: string
  attivo: boolean
  note: string
}

interface SedeForm {
  descrizione: string
  via: string
  citta: string
  cap: string
  provincia: string
  note: string
  is_preferita: boolean
}

interface ReferenteForm {
  nome: string
  cognome: string
  email: string
  telefono: string
  ruolo: string
  note: string
  is_preferito: boolean
}

const emptyForm: ClienteForm = {
  ragione_sociale: '',
  p_iva: '',
  via: '',
  citta: '',
  cap: '',
  provincia: '',
  email: '',
  telefono: '',
  attivo: true,
  note: '',
}

const emptySedeForm: SedeForm = {
  descrizione: '',
  via: '',
  citta: '',
  cap: '',
  provincia: '',
  note: '',
  is_preferita: false,
}

const emptyReferenteForm: ReferenteForm = {
  nome: '',
  cognome: '',
  email: '',
  telefono: '',
  ruolo: '',
  note: '',
  is_preferito: false,
}

export default function ClientiPage() {
  const [clienti, setClienti] = useState<Cliente[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingCliente, setEditingCliente] = useState<Cliente | null>(null)
  const [form, setForm] = useState<ClienteForm>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Toast e Confirm
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Cliente | null>(null)

  // Sub-modali
  const [showSedi, setShowSedi] = useState(false)
  const [showReferenti, setShowReferenti] = useState(false)
  const [sedi, setSedi] = useState<SedeCliente[]>([])
  const [referenti, setReferenti] = useState<ReferenteCliente[]>([])
  const [sedeForm, setSedeForm] = useState<SedeForm>(emptySedeForm)
  const [editingSede, setEditingSede] = useState<SedeCliente | null>(null)
  const [referenteForm, setReferenteForm] = useState<ReferenteForm>(emptyReferenteForm)
  const [editingReferente, setEditingReferente] = useState<ReferenteCliente | null>(null)
  const [savingSub, setSavingSub] = useState(false)

  useEffect(() => {
    loadClienti()
  }, [])

  const loadClienti = async () => {
    try {
      setLoading(true)
      const response = await api.get('/v1/anagrafiche/clienti')
      setClienti(response.data)
    } catch (err) {
      console.error('Errore caricamento clienti:', err)
      setToast({ message: 'Errore nel caricamento dei clienti', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setEditingCliente(null)
    setForm(emptyForm)
    setError('')
    setShowModal(true)
  }

  const handleEdit = (cliente: Cliente) => {
    setEditingCliente(cliente)
    setForm({
      ragione_sociale: cliente.ragione_sociale,
      p_iva: cliente.p_iva || '',
      via: cliente.via || '',
      citta: cliente.citta || '',
      cap: cliente.cap || '',
      provincia: cliente.provincia || '',
      email: cliente.email || '',
      telefono: cliente.telefono || '',
      attivo: cliente.attivo,
      note: cliente.note || '',
    })
    setError('')
    setShowModal(true)
  }

  const handleDeleteClick = (cliente: Cliente) => {
    setConfirmDelete(cliente)
  }

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return

    try {
      await api.delete(`/v1/anagrafiche/clienti/${confirmDelete.id}`)
      setToast({ message: 'Cliente eliminato con successo', type: 'success' })
      loadClienti()
    } catch (err) {
      console.error('Errore eliminazione:', err)
      setToast({ message: 'Errore durante l\'eliminazione', type: 'error' })
    } finally {
      setConfirmDelete(null)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSaving(true)

    try {
      const data = {
        ragione_sociale: form.ragione_sociale,
        p_iva: form.p_iva || null,
        via: form.via || null,
        citta: form.citta || null,
        cap: form.cap || null,
        provincia: form.provincia || null,
        email: form.email || null,
        telefono: form.telefono || null,
        attivo: form.attivo,
        note: form.note || null,
      }

      if (editingCliente) {
        await api.put(`/v1/anagrafiche/clienti/${editingCliente.id}`, data)
        setToast({ message: 'Cliente modificato con successo', type: 'success' })
      } else {
        await api.post('/v1/anagrafiche/clienti', data)
        setToast({ message: 'Cliente creato con successo', type: 'success' })
      }

      setShowModal(false)
      loadClienti()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      setError(error.response?.data?.detail || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  // --- Sedi ---
  const openSedi = async (cliente: Cliente) => {
    setEditingCliente(cliente)
    try {
      const response = await api.get(`/v1/anagrafiche/clienti/${cliente.id}/sedi`)
      setSedi(response.data)
    } catch {
      setSedi(cliente.sedi || [])
    }
    setSedeForm(emptySedeForm)
    setEditingSede(null)
    setShowSedi(true)
  }

  const handleSaveSede = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingCliente) return
    setSavingSub(true)

    try {
      const data = {
        descrizione: sedeForm.descrizione,
        via: sedeForm.via || null,
        citta: sedeForm.citta || null,
        cap: sedeForm.cap || null,
        provincia: sedeForm.provincia || null,
        note: sedeForm.note || null,
        is_preferita: sedeForm.is_preferita,
      }

      if (editingSede) {
        await api.put(`/v1/anagrafiche/clienti/${editingCliente.id}/sedi/${editingSede.id}`, data)
        setToast({ message: 'Sede aggiornata', type: 'success' })
      } else {
        await api.post(`/v1/anagrafiche/clienti/${editingCliente.id}/sedi`, data)
        setToast({ message: 'Sede aggiunta', type: 'success' })
      }

      const response = await api.get(`/v1/anagrafiche/clienti/${editingCliente.id}/sedi`)
      setSedi(response.data)
      setSedeForm(emptySedeForm)
      setEditingSede(null)
      loadClienti()
    } catch {
      setToast({ message: 'Errore nel salvataggio sede', type: 'error' })
    } finally {
      setSavingSub(false)
    }
  }

  const handleEditSede = (sede: SedeCliente) => {
    setEditingSede(sede)
    setSedeForm({
      descrizione: sede.descrizione,
      via: sede.via || '',
      citta: sede.citta || '',
      cap: sede.cap || '',
      provincia: sede.provincia || '',
      note: sede.note || '',
      is_preferita: sede.is_preferita || false,
    })
  }

  const handleDeleteSede = async (sedeId: string) => {
    if (!editingCliente) return
    try {
      await api.delete(`/v1/anagrafiche/clienti/${editingCliente.id}/sedi/${sedeId}`)
      const response = await api.get(`/v1/anagrafiche/clienti/${editingCliente.id}/sedi`)
      setSedi(response.data)
      setToast({ message: 'Sede eliminata', type: 'success' })
      loadClienti()
    } catch {
      setToast({ message: 'Errore eliminazione sede', type: 'error' })
    }
  }

  // --- Referenti ---
  const openReferenti = async (cliente: Cliente) => {
    setEditingCliente(cliente)
    try {
      const response = await api.get(`/v1/anagrafiche/clienti/${cliente.id}/referenti`)
      setReferenti(response.data)
    } catch {
      setReferenti(cliente.referenti || [])
    }
    setReferenteForm(emptyReferenteForm)
    setEditingReferente(null)
    setShowReferenti(true)
  }

  const handleSaveReferente = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingCliente) return
    setSavingSub(true)

    try {
      const data = {
        nome: referenteForm.nome,
        cognome: referenteForm.cognome,
        email: referenteForm.email || null,
        telefono: referenteForm.telefono || null,
        ruolo: referenteForm.ruolo || null,
        note: referenteForm.note || null,
        is_preferito: referenteForm.is_preferito,
      }

      if (editingReferente) {
        await api.put(`/v1/anagrafiche/clienti/${editingCliente.id}/referenti/${editingReferente.id}`, data)
        setToast({ message: 'Referente aggiornato', type: 'success' })
      } else {
        await api.post(`/v1/anagrafiche/clienti/${editingCliente.id}/referenti`, data)
        setToast({ message: 'Referente aggiunto', type: 'success' })
      }

      const response = await api.get(`/v1/anagrafiche/clienti/${editingCliente.id}/referenti`)
      setReferenti(response.data)
      setReferenteForm(emptyReferenteForm)
      setEditingReferente(null)
      loadClienti()
    } catch {
      setToast({ message: 'Errore nel salvataggio referente', type: 'error' })
    } finally {
      setSavingSub(false)
    }
  }

  const handleEditReferente = (ref: ReferenteCliente) => {
    setEditingReferente(ref)
    setReferenteForm({
      nome: ref.nome,
      cognome: ref.cognome,
      email: ref.email || '',
      telefono: ref.telefono || '',
      ruolo: ref.ruolo || '',
      note: ref.note || '',
      is_preferito: ref.is_preferito || false,
    })
  }

  const handleDeleteReferente = async (refId: string) => {
    if (!editingCliente) return
    try {
      await api.delete(`/v1/anagrafiche/clienti/${editingCliente.id}/referenti/${refId}`)
      const response = await api.get(`/v1/anagrafiche/clienti/${editingCliente.id}/referenti`)
      setReferenti(response.data)
      setToast({ message: 'Referente eliminato', type: 'success' })
      loadClienti()
    } catch {
      setToast({ message: 'Errore eliminazione referente', type: 'error' })
    }
  }

  const filteredClienti = clienti.filter(c =>
    c.ragione_sociale.toLowerCase().includes(search.toLowerCase()) ||
    (c.p_iva || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.email?.toLowerCase() || '').includes(search.toLowerCase()) ||
    (c.telefono || '').includes(search)
  )

  return (
    <div>
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Elimina Cliente"
        message={`Sei sicuro di voler eliminare "${confirmDelete?.ragione_sociale}"?`}
        confirmText="Elimina"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDelete(null)}
        variant="danger"
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clienti</h1>
          <p className="text-gray-500">Anagrafica clienti dell'azienda</p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nuovo Cliente
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Cerca clienti..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Caricamento...</div>
        ) : filteredClienti.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {search ? 'Nessun cliente trovato' : 'Nessun cliente presente'}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ragione Sociale</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">P.IVA</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Citta</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Telefono</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredClienti.map((cliente) => (
                <tr key={cliente.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{cliente.ragione_sociale}</div>
                  </td>
                  <td className="px-6 py-4 text-gray-600 text-sm">{cliente.p_iva || '-'}</td>
                  <td className="px-6 py-4 text-gray-600 text-sm">
                    {cliente.citta ? (
                      <>
                        {cliente.citta}
                        {cliente.provincia && <span className="text-gray-400"> ({cliente.provincia})</span>}
                      </>
                    ) : '-'}
                  </td>
                  <td className="px-6 py-4 text-gray-600 text-sm">{cliente.email || '-'}</td>
                  <td className="px-6 py-4 text-gray-600 text-sm">{cliente.telefono || '-'}</td>
                  <td className="px-6 py-4">
                    <span className={`flex items-center gap-1 text-sm ${
                      cliente.attivo ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {cliente.attivo ? (
                        <><Check className="w-4 h-4" /> Attivo</>
                      ) : (
                        <><X className="w-4 h-4" /> Inattivo</>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openSedi(cliente)}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Gestisci Sedi"
                      >
                        <MapPin className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openReferenti(cliente)}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Gestisci Referenti"
                      >
                        <Users className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(cliente)}
                        className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                        title="Modifica"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(cliente)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                        title="Elimina"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal Cliente */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingCliente ? 'Modifica Cliente' : 'Nuovo Cliente'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ragione Sociale *
                </label>
                <input
                  type="text"
                  required
                  value={form.ragione_sociale}
                  onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Partita IVA
                </label>
                <input
                  type="text"
                  value={form.p_iva}
                  onChange={(e) => setForm({ ...form, p_iva: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="12345678901"
                />
              </div>

              {/* Sede Legale */}
              <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-700">Sede Legale</h3>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Via</label>
                  <input
                    type="text"
                    value={form.via}
                    onChange={(e) => setForm({ ...form, via: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-1">
                    <label className="block text-sm text-gray-600 mb-1">CAP</label>
                    <input
                      type="text"
                      value={form.cap}
                      onChange={(e) => setForm({ ...form, cap: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      maxLength={5}
                    />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm text-gray-600 mb-1">Citta</label>
                    <input
                      type="text"
                      value={form.citta}
                      onChange={(e) => setForm({ ...form, citta: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm text-gray-600 mb-1">Prov.</label>
                    <input
                      type="text"
                      value={form.provincia}
                      onChange={(e) => setForm({ ...form, provincia: e.target.value.toUpperCase() })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      maxLength={2}
                      placeholder="MI"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefono
                  </label>
                  <input
                    type="tel"
                    value={form.telefono}
                    onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note
                </label>
                <textarea
                  rows={3}
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                />
              </div>

              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.attivo}
                    onChange={(e) => setForm({ ...form, attivo: e.target.checked })}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Cliente attivo</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Sedi */}
      {showSedi && editingCliente && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Sedi - {editingCliente.ragione_sociale}
              </h2>
              <button
                onClick={() => setShowSedi(false)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {/* Lista sedi esistenti */}
              {sedi.length > 0 && (
                <div className="space-y-3 mb-6">
                  {sedi.map((sede) => (
                    <div key={sede.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900 flex items-center gap-1.5">
                          {sede.is_preferita && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />}
                          {sede.descrizione}
                        </div>
                        <div className="text-sm text-gray-500">
                          {[sede.via, sede.cap, sede.citta, sede.provincia ? `(${sede.provincia})` : ''].filter(Boolean).join(', ')}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleEditSede(sede)}
                          className="p-1.5 text-gray-400 hover:text-primary-600"
                          title="Modifica"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteSede(sede.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600"
                          title="Elimina"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Form nuova/modifica sede */}
              <form onSubmit={handleSaveSede} className="border border-gray-200 rounded-lg p-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-700">
                  {editingSede ? 'Modifica Sede' : 'Nuova Sede'}
                </h3>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Descrizione *</label>
                  <input
                    type="text"
                    required
                    value={sedeForm.descrizione}
                    onChange={(e) => setSedeForm({ ...sedeForm, descrizione: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Magazzino Nord, Cantiere Roma..."
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Via</label>
                  <input
                    type="text"
                    value={sedeForm.via}
                    onChange={(e) => setSedeForm({ ...sedeForm, via: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">CAP</label>
                    <input
                      type="text"
                      value={sedeForm.cap}
                      onChange={(e) => setSedeForm({ ...sedeForm, cap: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      maxLength={5}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Citta</label>
                    <input
                      type="text"
                      value={sedeForm.citta}
                      onChange={(e) => setSedeForm({ ...sedeForm, citta: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Prov.</label>
                    <input
                      type="text"
                      value={sedeForm.provincia}
                      onChange={(e) => setSedeForm({ ...sedeForm, provincia: e.target.value.toUpperCase() })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      maxLength={2}
                      placeholder="MI"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between pt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sedeForm.is_preferita}
                      onChange={(e) => setSedeForm({ ...sedeForm, is_preferita: e.target.checked })}
                      className="w-4 h-4 text-amber-500 rounded border-gray-300 focus:ring-amber-500"
                    />
                    <Star className={`w-3.5 h-3.5 ${sedeForm.is_preferita ? 'text-amber-500 fill-amber-500' : 'text-gray-400'}`} />
                    <span className="text-sm text-gray-700">Sede preferita</span>
                  </label>
                  <div className="flex gap-2">
                  {editingSede && (
                    <button
                      type="button"
                      onClick={() => { setEditingSede(null); setSedeForm(emptySedeForm) }}
                      className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                    >
                      Annulla
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={savingSub}
                    className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    {savingSub ? 'Salvataggio...' : editingSede ? 'Aggiorna' : 'Aggiungi'}
                  </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Referenti */}
      {showReferenti && editingCliente && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Referenti - {editingCliente.ragione_sociale}
              </h2>
              <button
                onClick={() => setShowReferenti(false)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {/* Lista referenti esistenti */}
              {referenti.length > 0 && (
                <div className="space-y-3 mb-6">
                  {referenti.map((ref) => (
                    <div key={ref.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900 flex items-center gap-1.5">
                          {ref.is_preferito && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />}
                          {ref.cognome} {ref.nome}
                        </div>
                        {ref.ruolo && <div className="text-sm text-primary-600">{ref.ruolo}</div>}
                        <div className="text-sm text-gray-500">
                          {[ref.email, ref.telefono].filter(Boolean).join(' | ')}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleEditReferente(ref)}
                          className="p-1.5 text-gray-400 hover:text-primary-600"
                          title="Modifica"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteReferente(ref.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600"
                          title="Elimina"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Form nuovo/modifica referente */}
              <form onSubmit={handleSaveReferente} className="border border-gray-200 rounded-lg p-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-700">
                  {editingReferente ? 'Modifica Referente' : 'Nuovo Referente'}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Nome *</label>
                    <input
                      type="text"
                      required
                      value={referenteForm.nome}
                      onChange={(e) => setReferenteForm({ ...referenteForm, nome: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Cognome *</label>
                    <input
                      type="text"
                      required
                      value={referenteForm.cognome}
                      onChange={(e) => setReferenteForm({ ...referenteForm, cognome: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Email</label>
                    <input
                      type="email"
                      value={referenteForm.email}
                      onChange={(e) => setReferenteForm({ ...referenteForm, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Telefono</label>
                    <input
                      type="tel"
                      value={referenteForm.telefono}
                      onChange={(e) => setReferenteForm({ ...referenteForm, telefono: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Ruolo</label>
                  <input
                    type="text"
                    value={referenteForm.ruolo}
                    onChange={(e) => setReferenteForm({ ...referenteForm, ruolo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Resp. Acquisti, Dir. Tecnico..."
                  />
                </div>
                <div className="flex items-center justify-between pt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={referenteForm.is_preferito}
                      onChange={(e) => setReferenteForm({ ...referenteForm, is_preferito: e.target.checked })}
                      className="w-4 h-4 text-amber-500 rounded border-gray-300 focus:ring-amber-500"
                    />
                    <Star className={`w-3.5 h-3.5 ${referenteForm.is_preferito ? 'text-amber-500 fill-amber-500' : 'text-gray-400'}`} />
                    <span className="text-sm text-gray-700">Referente preferito</span>
                  </label>
                  <div className="flex gap-2">
                  {editingReferente && (
                    <button
                      type="button"
                      onClick={() => { setEditingReferente(null); setReferenteForm(emptyReferenteForm) }}
                      className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                    >
                      Annulla
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={savingSub}
                    className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    {savingSub ? 'Salvataggio...' : editingReferente ? 'Aggiorna' : 'Aggiungi'}
                  </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
